import { Link, useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchMe, type MeUser } from "../lib/fetch-me";
import {
  fetchTransporterProfile,
  fetchTransporterTeamUsers,
  patchTransporterKybProfile,
  type TransporterKybPatchBody,
  type TransporterProfileDto,
  type TransporterTeamUserDto,
} from "../lib/fetch-transporter-delivery-ops";
import {
  createTransporterBankLinkToken,
  exchangeTransporterBankPublicToken,
  formatTransporterBankLinkExchangeError,
  formatTransporterBankLinkStepError,
} from "../lib/transporter-plaid-bank-client";
import { TransporterPlaidLinkButton } from "../components/TransporterPlaidLinkButton";
import { TransporterOnboardingOrgReadonlySummary } from "../workspace/onboarding-readonly-summary";
import { useTransporterWorkspace } from "../workspace/transporter-workspace-context";

function isoDateSlice(iso: string): string {
  return iso.length >= 10 ? iso.slice(0, 10) : iso;
}

function profileToForm(p: TransporterProfileDto): TransporterKybPatchBody {
  return {
    legalName: p.legalName,
    dbaName: p.dbaName ?? null,
    addressLine1: p.addressLine1,
    addressLine2: p.addressLine2 ?? null,
    city: p.city,
    state: p.state,
    zip: p.zip,
    phone: p.phone,
    email: p.email,
    medLicenseNumber: p.medLicenseNumber,
    medLicenseExpiresAt: isoDateSlice(p.medLicenseExpiresAt),
    hasDeliveryPermit: p.hasDeliveryPermit,
    deliveryPermitNumber: p.deliveryPermitNumber ?? null,
    deliveryPermitExpiresAt: p.deliveryPermitExpiresAt ? isoDateSlice(p.deliveryPermitExpiresAt) : null,
  };
}

function formsEqual(a: TransporterKybPatchBody, b: TransporterKybPatchBody): boolean {
  return (
    a.legalName === b.legalName &&
    (a.dbaName ?? "") === (b.dbaName ?? "") &&
    a.addressLine1 === b.addressLine1 &&
    (a.addressLine2 ?? "") === (b.addressLine2 ?? "") &&
    a.city === b.city &&
    a.state === b.state &&
    a.zip === b.zip &&
    a.phone === b.phone &&
    a.email === b.email &&
    a.medLicenseNumber === b.medLicenseNumber &&
    a.medLicenseExpiresAt === b.medLicenseExpiresAt &&
    a.hasDeliveryPermit === b.hasDeliveryPermit &&
    (a.deliveryPermitNumber ?? "") === (b.deliveryPermitNumber ?? "") &&
    (a.deliveryPermitExpiresAt ?? "") === (b.deliveryPermitExpiresAt ?? "")
  );
}

function kybAccountFormValid(f: TransporterKybPatchBody): string | null {
  if (!f.medLicenseNumber.trim()) return "MED license number is required.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.medLicenseExpiresAt.trim().slice(0, 10)))
    return "MED license expiry must be a valid date (YYYY-MM-DD).";
  if (f.hasDeliveryPermit) {
    if (!f.deliveryPermitNumber?.trim()) return "Delivery permit number is required when you have a delivery permit.";
    const pe = f.deliveryPermitExpiresAt?.trim().slice(0, 10) ?? "";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(pe))
      return "Delivery permit expiry must be a valid date (YYYY-MM-DD).";
  }
  return null;
}

type AccountTab = "org" | "payout" | "team";

function useAccountTab(): AccountTab {
  return useRouterState({
    select: (s) => {
      const t = (s.location.search as { tab?: unknown }).tab;
      if (t === "payout" || t === "team" || t === "org") return t;
      return "org";
    },
  });
}

function tabLinkCls(active: boolean): string {
  return active
    ? "rounded-md border border-green/25 bg-green-pale px-3 py-1.5 text-sm font-medium text-green ring-1 ring-green/15"
    : "rounded-md border border-transparent px-3 py-1.5 text-sm text-gray hover:border-border hover:text-dark";
}

export function AccountScreen() {
  const { transporterId, getToken, refreshWorkspaceProfile } = useTransporterWorkspace();
  const tab = useAccountTab();
  const [me, setMe] = useState<MeUser | null>(null);

  const [profile, setProfile] = useState<TransporterProfileDto | null>(null);
  const [form, setForm] = useState<TransporterKybPatchBody | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState(false);
  const [plaidLinkToken, setPlaidLinkToken] = useState<string | null>(null);
  const [plaidTokenLoading, setPlaidTokenLoading] = useState(false);
  const [plaidTokenErr, setPlaidTokenErr] = useState<string | null>(null);
  const [plaidExchangeBusy, setPlaidExchangeBusy] = useState(false);
  const [plaidExchangeErr, setPlaidExchangeErr] = useState<string | null>(null);
  const [plaidExchangeOk, setPlaidExchangeOk] = useState(false);
  const [teamUsers, setTeamUsers] = useState<TransporterTeamUserDto[] | null>(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);

  useEffect(() => {
    void fetchMe(getToken)
      .then((r) => setMe(r.user))
      .catch(() => setMe(null));
  }, [getToken]);

  const load = useCallback(async () => {
    if (!transporterId) return;
    setLoading(true);
    setLoadError(null);
    setSaveOk(false);
    setPlaidExchangeOk(false);
    setPlaidExchangeErr(null);
    try {
      const p = await fetchTransporterProfile(getToken, transporterId);
      setProfile(p);
      setForm(profileToForm(p));
    } catch (e) {
      setProfile(null);
      setForm(null);
      setLoadError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [getToken, transporterId]);

  useEffect(() => {
    if (!transporterId) {
      setProfile(null);
      setForm(null);
      setLoadError(null);
      setLoading(false);
      return;
    }
    void load();
  }, [load, transporterId]);

  useEffect(() => {
    if (!transporterId || tab !== "team") return;
    let cancelled = false;
    setTeamLoading(true);
    setTeamError(null);
    void (async () => {
      try {
        const users = await fetchTransporterTeamUsers(getToken, transporterId);
        if (!cancelled) {
          setTeamUsers(users);
        }
      } catch (e) {
        if (!cancelled) {
          setTeamUsers(null);
          setTeamError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled) setTeamLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, transporterId, tab]);

  useEffect(() => {
    if (tab !== "payout" || !transporterId) {
      setPlaidLinkToken(null);
      setPlaidTokenErr(null);
      setPlaidTokenLoading(false);
      return;
    }
    let cancelled = false;
    setPlaidTokenLoading(true);
    setPlaidTokenErr(null);
    void createTransporterBankLinkToken(getToken, transporterId)
      .then((r) => {
        if (!cancelled) setPlaidLinkToken(r.linkToken);
      })
      .catch((e) => {
        if (!cancelled) setPlaidTokenErr(formatTransporterBankLinkStepError(e));
      })
      .finally(() => {
        if (!cancelled) setPlaidTokenLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, transporterId, getToken]);

  const onPlaidBankSuccess = useCallback(
    async (publicToken: string, metadata: { accounts?: { id: string }[] }) => {
      if (!transporterId) return;
      const accountId = metadata.accounts?.[0]?.id;
      setPlaidExchangeBusy(true);
      setPlaidExchangeErr(null);
      setPlaidExchangeOk(false);
      try {
        const updated = await exchangeTransporterBankPublicToken(
          getToken,
          transporterId,
          publicToken,
          accountId,
        );
        setProfile(updated);
        setForm(profileToForm(updated));
        setPlaidExchangeOk(true);
        void refreshWorkspaceProfile();
      } catch (e) {
        setPlaidExchangeErr(formatTransporterBankLinkExchangeError(e));
      } finally {
        setPlaidExchangeBusy(false);
      }
    },
    [getToken, transporterId, refreshWorkspaceProfile],
  );

  const dirty = useMemo(() => {
    if (!profile || !form) return false;
    return !formsEqual(form, profileToForm(profile));
  }, [profile, form]);

  async function onSaveOrg(e: React.FormEvent) {
    e.preventDefault();
    if (!transporterId || !form || saving) return;
    setSaveError(null);
    setSaveOk(false);
    if (profile && formsEqual(form, profileToForm(profile))) {
      setSaveError("No changes to save.");
      return;
    }
    const permitExp =
      form.hasDeliveryPermit && form.deliveryPermitExpiresAt && form.deliveryPermitExpiresAt.trim().length >= 10
        ? form.deliveryPermitExpiresAt.trim().slice(0, 10)
        : null;
    const body: TransporterKybPatchBody = {
      legalName: form.legalName.trim(),
      dbaName: form.dbaName?.trim() ? form.dbaName.trim() : null,
      addressLine1: form.addressLine1.trim(),
      addressLine2: form.addressLine2?.trim() ? form.addressLine2.trim() : null,
      city: form.city.trim(),
      state: form.state.trim().toUpperCase().slice(0, 2),
      zip: form.zip.trim().replace(/\D/g, "").slice(0, 5),
      phone: form.phone.trim(),
      email: form.email.trim(),
      medLicenseNumber: form.medLicenseNumber.trim(),
      medLicenseExpiresAt: form.medLicenseExpiresAt.trim().slice(0, 10),
      hasDeliveryPermit: form.hasDeliveryPermit,
      deliveryPermitNumber:
        form.hasDeliveryPermit && form.deliveryPermitNumber?.trim()
          ? form.deliveryPermitNumber.trim()
          : null,
      deliveryPermitExpiresAt: form.hasDeliveryPermit ? permitExp : null,
    };
    const v = kybAccountFormValid(body);
    if (v) {
      setSaveError(v);
      return;
    }
    setSaving(true);
    try {
      const updated = await patchTransporterKybProfile(getToken, transporterId, body);
      setProfile(updated);
      setForm(profileToForm(updated));
      setSaveOk(true);
      void refreshWorkspaceProfile();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  if (!transporterId) {
    return (
      <div className="p-4 space-y-4 max-w-lg">
        <p className="text-sm text-gray">
          Choose a transporter in the scope panel (platform admin) or sign in with a dispatcher account linked to a
          transporter.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 max-w-3xl">
      <p className="text-sm text-gray">Transporter profile, payout settings, and team roster.</p>

      <nav className="flex flex-wrap gap-2" aria-label="Account sections">
        <Link to="/workspace/account" search={{ tab: "org" }} className={tabLinkCls(tab === "org")}>
          Organization
        </Link>
        <Link to="/workspace/account" search={{ tab: "payout" }} className={tabLinkCls(tab === "payout")}>
          Payout & bank
        </Link>
        <Link to="/workspace/account" search={{ tab: "team" }} className={tabLinkCls(tab === "team")}>
          Team
        </Link>
      </nav>

      {loading ? (
        <p className="text-sm text-gray">Loading…</p>
      ) : loadError ? (
        <div className="rounded-md border border-red/40 bg-red-light p-3 text-sm space-y-2">
          <p className="text-red">Could not load account data. Try again.</p>
          <button type="button" className="text-sm underline" onClick={() => void load()}>
            Retry
          </button>
        </div>
      ) : profile && form ? (
        <>
          {tab === "org" ? (
            <div className="space-y-6">
              <TransporterOnboardingOrgReadonlySummary />

              <form onSubmit={(e) => void onSaveOrg(e)} className="td-surface space-y-4 p-6">
                <h2 className="font-medium text-dark">Organization &amp; contact</h2>
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="acct-legal">
                    Legal name
                  </label>
                  <input
                    id="acct-legal"
                    className="input-field"
                    value={form.legalName}
                    onChange={(e) => setForm((f) => (f ? { ...f, legalName: e.target.value } : f))}
                    required
                    minLength={1}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="acct-dba">
                    DBA (optional)
                  </label>
                  <input
                    id="acct-dba"
                    className="input-field"
                    value={form.dbaName ?? ""}
                    onChange={(e) =>
                      setForm((f) => (f ? { ...f, dbaName: e.target.value || null } : f))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="acct-addr1">
                    Address line 1
                  </label>
                  <input
                    id="acct-addr1"
                    className="input-field"
                    value={form.addressLine1}
                    onChange={(e) => setForm((f) => (f ? { ...f, addressLine1: e.target.value } : f))}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="acct-addr2">
                    Address line 2
                  </label>
                  <input
                    id="acct-addr2"
                    className="input-field"
                    value={form.addressLine2 ?? ""}
                    onChange={(e) =>
                      setForm((f) => (f ? { ...f, addressLine2: e.target.value || null } : f))
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-sm font-medium" htmlFor="acct-city">
                      City
                    </label>
                    <input
                      id="acct-city"
                      className="input-field"
                      value={form.city}
                      onChange={(e) => setForm((f) => (f ? { ...f, city: e.target.value } : f))}
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium" htmlFor="acct-state">
                      State
                    </label>
                    <input
                      id="acct-state"
                      className="input-field uppercase"
                      maxLength={2}
                      value={form.state}
                      onChange={(e) =>
                        setForm((f) => (f ? { ...f, state: e.target.value.toUpperCase().slice(0, 2) } : f))
                      }
                      required
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="acct-zip">
                    ZIP
                  </label>
                  <input
                    id="acct-zip"
                    className="input-field"
                    inputMode="numeric"
                    pattern="\d{5}"
                    title="5-digit ZIP"
                    value={form.zip}
                    onChange={(e) =>
                      setForm((f) => (f ? { ...f, zip: e.target.value.replace(/\D/g, "").slice(0, 5) } : f))
                    }
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="acct-phone">
                    Phone
                  </label>
                  <input
                    id="acct-phone"
                    type="tel"
                    className="input-field"
                    value={form.phone}
                    onChange={(e) => setForm((f) => (f ? { ...f, phone: e.target.value } : f))}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="acct-email">
                    Email
                  </label>
                  <input
                    id="acct-email"
                    type="email"
                    className="input-field"
                    value={form.email}
                    onChange={(e) => setForm((f) => (f ? { ...f, email: e.target.value } : f))}
                    required
                  />
                </div>

                <h2 className="font-medium text-dark pt-2 border-t border-border">Compliance &amp; licensing</h2>
                <p className="text-xs text-gray">
                  Colorado MED and delivery permit must match what regulators expect for retail cannabis transport.
                </p>
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="acct-med">
                    MED license number
                  </label>
                  <input
                    id="acct-med"
                    className="input-field font-mono"
                    value={form.medLicenseNumber}
                    onChange={(e) => setForm((f) => (f ? { ...f, medLicenseNumber: e.target.value } : f))}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="acct-med-exp">
                    MED license expires
                  </label>
                  <input
                    id="acct-med-exp"
                    type="date"
                    className="input-field font-mono"
                    value={form.medLicenseExpiresAt}
                    onChange={(e) => setForm((f) => (f ? { ...f, medLicenseExpiresAt: e.target.value } : f))}
                    required
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-gray">
                    <input
                      type="checkbox"
                      checked={form.hasDeliveryPermit}
                      onChange={(e) =>
                        setForm((f) =>
                          f ? { ...f, hasDeliveryPermit: e.target.checked } : f,
                        )
                      }
                      className="rounded border-border"
                    />
                    Has Colorado cannabis delivery permit
                  </label>
                </div>
                {form.hasDeliveryPermit ? (
                  <>
                    <div className="space-y-1">
                      <label className="text-sm font-medium" htmlFor="acct-del-permit">
                        Delivery permit number
                      </label>
                      <input
                        id="acct-del-permit"
                        className="input-field font-mono"
                        value={form.deliveryPermitNumber ?? ""}
                        onChange={(e) =>
                          setForm((f) =>
                            f ? { ...f, deliveryPermitNumber: e.target.value || null } : f,
                          )
                        }
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm font-medium" htmlFor="acct-del-exp">
                        Delivery permit expires
                      </label>
                      <input
                        id="acct-del-exp"
                        type="date"
                        className="input-field font-mono"
                        value={form.deliveryPermitExpiresAt ?? ""}
                        onChange={(e) =>
                          setForm((f) =>
                            f
                              ? { ...f, deliveryPermitExpiresAt: e.target.value || null }
                              : f,
                          )
                        }
                      />
                    </div>
                  </>
                ) : null}

                {saveError ? <p className="text-sm text-red">{saveError}</p> : null}
                {saveOk ? <p className="text-sm text-success">Saved.</p> : null}

                <div className="flex gap-2 pt-2">
                  <button
                    type="submit"
                    disabled={saving || !dirty}
                    className="btn-primary disabled:opacity-50"
                  >
                    {saving ? "Saving…" : "Save changes"}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={saving || !dirty}
                    onClick={() => {
                      if (profile) {
                        setForm(profileToForm(profile));
                        setSaveError(null);
                        setSaveOk(false);
                      }
                    }}
                  >
                    Reset
                  </button>
                </div>
              </form>
            </div>
          ) : null}

          {tab === "payout" ? (
            <div className="td-surface space-y-4 p-6">
              <h2 className="font-medium text-dark">Payout &amp; bank (Plaid)</h2>
              <p className="text-xs text-gray">
                Link a checking account with Plaid. The API stores Plaid tokens encrypted, sets bank display name and
                last four, and when Straddle is configured creates a settlement paykey for payouts.
              </p>
              <dl className="grid gap-2 text-sm text-gray">
                <div className="flex flex-wrap justify-between gap-2 border-b border-border pb-2">
                  <dt className="text-gray">Linked account</dt>
                  <dd className="text-dark">
                    {profile.bankName?.trim() ? profile.bankName.trim() : "—"}
                    {profile.bankAccountLast4 ? (
                      <span className="ml-2 font-mono text-gray">·•••{profile.bankAccountLast4}</span>
                    ) : null}
                  </dd>
                </div>
                <div className="flex flex-wrap justify-between gap-2 border-b border-border pb-2">
                  <dt className="text-gray">Straddle</dt>
                  <dd className="font-mono text-xs text-gray">
                    {profile.straddleAccountId?.trim() ? profile.straddleAccountId.trim() : "—"}
                  </dd>
                </div>
                <div className="flex flex-wrap justify-between gap-2">
                  <dt className="text-gray">Paykey ready</dt>
                  <dd>{profile.secrets.straddlePaykeyConfigured ? "yes" : "no"}</dd>
                </div>
                <div className="flex flex-wrap justify-between gap-2">
                  <dt className="text-gray">Plaid linked</dt>
                  <dd>
                    {profile.secrets.plaidItemConfigured && profile.secrets.plaidAccessTokenConfigured
                      ? "yes"
                      : "no"}
                  </dd>
                </div>
              </dl>

              {plaidTokenErr ? (
                <p className="text-sm text-red">{plaidTokenErr}</p>
              ) : plaidTokenLoading ? (
                <p className="text-sm text-gray">Preparing Plaid…</p>
              ) : plaidLinkToken ? (
                <div className="space-y-2">
                  <TransporterPlaidLinkButton
                    linkToken={plaidLinkToken}
                    disabled={plaidExchangeBusy}
                    label={plaidExchangeBusy ? "Saving…" : "Link bank with Plaid"}
                    onPlaidSuccess={(publicToken, metadata) => {
                      void onPlaidBankSuccess(publicToken, metadata);
                    }}
                  />
                  <p className="text-xs text-gray">
                    Opens Plaid Link. Choose a checking account used for payouts. If Straddle returns an error, confirm
                    your phone in Organization is in E.164 format (e.g. +15551234567).
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray">Could not load Plaid.</p>
              )}

              {plaidExchangeErr ? <p className="text-sm text-red">{plaidExchangeErr}</p> : null}
              {plaidExchangeOk ? (
                <p className="text-sm text-success">Bank linked and saved.</p>
              ) : null}
            </div>
          ) : null}

          {tab === "team" ? (
            <section className="td-surface space-y-4 p-6">
              <h2 className="font-medium text-dark">Team</h2>
              <p className="text-sm text-gray">
                Dispatchers and drivers linked to this transporter. Clerk user ids are not shown in this view.
              </p>
              {me?.role === "platform_admin" ? (
                <p className="text-sm text-gray">
                  As platform admin, add teammates through the admin users API after they exist in Clerk, using
                  dispatcher or driver roles and this transporter&apos;s scope.
                </p>
              ) : (
                <p className="text-sm text-gray">
                  New teammates must be invited in Clerk and provisioned by The Drop platform admin.
                </p>
              )}
              {teamLoading ? <p className="text-sm text-gray">Loading roster…</p> : null}
              {teamError ? <p className="text-sm text-red">Could not load the team list. Try again.</p> : null}
              {teamUsers && !teamLoading ? (
                <div className="td-table-wrap">
                  <table className="td-data-table min-w-[36rem]">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Role</th>
                        <th>Email</th>
                        <th>Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamUsers.map((u) => (
                        <tr key={u.id}>
                          <td className="text-sm font-medium text-[#333333]">{u.fullName}</td>
                          <td className="text-xs capitalize text-[#555555]">{u.role.replace(/_/g, " ")}</td>
                          <td className="text-sm text-[#555555]">{u.email}</td>
                          <td className="text-sm text-[#555555]">{u.isActive ? "yes" : "no"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
